import React from "react";
import { Modal, StyleSheet, TouchableOpacity, TouchableWithoutFeedback, View } from "react-native";
import { Box, HStack, Text, Image, VStack } from "native-base";
import { MenuListData } from "../lib/model";
import apps, { SaleMode } from "../lib/apps";
import { PALETTE } from "../lib/palette";
import { FLAG_STYLES, FlagKey } from "../lib/flags";
import { money } from "../lib/utils";
import ThumbImage from "./ThumbImage";

// Mockup pricerow (redesign-mockups.html 96-99). Normal price = PALETTE.PRICE
// (NOT accent), strikethrough = PALETTE.MUTED, sale price = PALETTE.SALE.
// 3-way sale logic (apps.isSale / apps.getPrice) is unchanged.
function Price({ saleMode, menu }: { saleMode: SaleMode, menu: MenuListData }) {
    if (apps.isSale(saleMode, menu)) {
        return (
            <HStack style={styles.priceRow}>
                <Text style={styles.priceOld}>{money(menu.price)}</Text>
                <Text style={styles.priceNow}>{money(apps.getPrice(saleMode, menu))}</Text>
            </HStack>
        );
    }

    return (
        <Text style={styles.priceNormal}>{money(menu.price)}</Text>
    );
}

// Dietary flag pills (mockup .flags/.flag 90-94). 색/라벨은 lib/flags 공용(리스트·상세 단일 소스).
function Flags({ m, compact }: { m: MenuListData; compact?: boolean }) {
    const on = [] as Array<FlagKey>;
    if (m.vegetarian) on.push("veg");
    if (m.gluetenFree) on.push("gf"); // keep existing misspelling
    if (m.spicy) on.push("spicy");
    if (!on.length && !m.popular) return null;
    // CARDS(compact)는 카드가 좁아 Veg+GF+Spicy+🔥 4개가 2줄로 넘쳐 잘렸음 → 칩·간격·🔥를 살짝 줄여 1줄에 맞춘다.
    const padH = compact ? 5 : 6;
    const fSize = compact ? 10 : 10;
    const fireSize = compact ? 13 : 14;
    return (
        <HStack style={{ flexWrap: "wrap", alignItems: "center" }} space={compact ? 0.5 : 1}>
            {on.map((k) => (
                <Box key={k} style={{ paddingHorizontal: padH, paddingVertical: 2, borderRadius: 6, backgroundColor: FLAG_STYLES[k].bg }}>
                    <Text style={{ fontSize: fSize, fontWeight: "800", letterSpacing: 0.3, color: FLAG_STYLES[k].color }}>{FLAG_STYLES[k].label}</Text>
                </Box>
            ))}
            {/* POPULAR(최근 주문 top10) — 식이 플래그 뒤 맨 마지막, 🔥 아이콘만. */}
            {m.popular && (
                <Text style={{ fontSize: fireSize }}>🔥</Text>
            )}
        </HStack>
    );
}

// Corner badges (mockup .badge 101-102). 할인 배지(HAPPY xor SALE)와 NEW는 별개 슬롯 —
// HAPPY/SALE은 서로 배타적이지만 NEW는 독립적이라 SALE+NEW / HAPPY+NEW가 함께 뜬다.
// 좌상단에 가로로 나란히 배치. (POPULAR은 코너가 아니라 Flags 칩 행 맨 뒤에 표시)
function Badge({ m, saleMode }: { m: MenuListData; saleMode: SaleMode }) {
    const badges: Array<{ label: string; bg: string }> = [];
    // NEW를 맨 앞에.
    if (m.newItem) { badges.push({ label: "NEW", bg: "#16a34a" }); }
    // 그다음 할인 배지: HAPPY 우선, 아니면 SALE (둘 중 하나만).
    if (apps.isSale(saleMode, m) && (m.happy1Only || m.happy2Only)) { badges.push({ label: "HAPPY", bg: "#7a3ff2" }); }
    else if (m.special && m.priceSp < m.price) { badges.push({ label: "SALE", bg: PALETTE.SALE }); }
    if (!badges.length) return null;
    return (
        <HStack style={{ position: "absolute", top: 6, left: 6, zIndex: 2 }} space={1}>
            {badges.map((b) => (
                <Box key={b.label} style={{ paddingHorizontal: 6, paddingVertical: 3, borderRadius: 6, backgroundColor: b.bg }}>
                    <Text style={{ fontSize: 9.5, fontWeight: "800", letterSpacing: 0.4, color: "#fff" }}>{b.label}</Text>
                </Box>
            ))}
        </HStack>
    );
}

// B1 (#4): 카드 고정 높이 슬롯. 카드의 정보영역을 고정 높이로 만들어 flag/desc 유무·줄수와
// 무관하게 모든 카드가 같은 높이·가격줄 위치를 갖게 한다. 스크롤 계약은 불변 —
// ROW_BASE/cardsThumbHFor를 renderMenuCard와 MenuList가 공유해 lockstep(중복 magic number 제거).
// ROW_BASE = INFO_H + ROW_GAP 로 renderMenuCard의 카드 고정높이와 MenuList의 ROW_H가 함께 움직인다.
// INFO_H를 줄이면 카드와 ROW_H(wrapper)가 같이 줄어 계약이 유지된다. dense 카드(2줄 이름+칩+2줄 설명+가격)
// 예산 = INFO_H - 패딩24 ≥ 내용 최대 ~122(gap 3) 여야 가격줄 clip 없음(현재 150 → 여유 4).
export const ROW_GAP = 12;   // 카드 사이 간격(card marginTop)
export const INFO_H = 150;   // 카드 정보영역 고정 높이(LIST=카드 전체높이, CARDS=thumb 아래 영역)
export const ROW_BASE = INFO_H + ROW_GAP;   // 162
export const cardsThumbHFor = (cols: number) => (cols >= 4 ? 140 : cols >= 3 ? 150 : 118);

// Shared mockup card (redesign-mockups.html .card 83-105). LIST = image-left row
// (.menu-rows 109-115), CARDS = image-top column (.menu-cards 117-126). Designed
// to fit within the fixed ROW_H used by getItemLayout (see MenuList) so the
// overflow:"hidden" wrapper never clips the bottom price row.
export function renderMenuCard(m: MenuListData, saleMode: SaleMode, shopid: string, onClick: (m: MenuListData) => void, design: "LIST" | "CARDS" = "LIST", cols: number = 1) {
    const isRow = design === "LIST";
    const thumbW = cols >= 2 ? 130 : 118;                          // LIST: 썸네일 너비(살짝 키움). 높이는 카드 전체(100%)
    const cardsThumbH = cardsThumbHFor(cols);                      // CARDS: image height by column count (shared)
    const anyFlag = !!(m.vegetarian || m.gluetenFree || m.spicy || m.popular);  // 칩 태그 유무(없으면 슬롯 미렌더 → 중앙정렬)
    return (
        <TouchableWithoutFeedback key={m.id} onPress={() => { if (!m.soldOut) { onClick(m); } }} disabled={m.soldOut}>
            <Box style={[styles.card, isRow ? styles.cardRow : styles.cardCol, { height: isRow ? INFO_H : cardsThumbH + INFO_H }]}>
                <Badge m={m} saleMode={saleMode} />
                {/* LIST 썸네일은 카드 높이에 꽉 차게(height 100%) — 하단 여백 제거. cover라 이미지가 잘려 채워짐. */}
                <Box style={[styles.cardThumb, isRow ? { width: thumbW, height: "100%" } : { width: "100%", height: cardsThumbH }]}>
                    <ThumbImage shopid={shopid} menuid={m.menuid} imageVer={m.imageVer} resizeMode="cover" style={{ width: "100%", height: "100%" }} />
                    {m.soldOut && (
                        <Image source={require("../../assets/soldout.png")} alt="Sold Out" resizeMode="contain" style={styles.soldOutOverlay} />
                    )}
                </Box>
                {/* 칩 → 이름 → 설명 → 가격, 세로 중앙 정렬. 없는 요소(칩/설명)는 렌더 안 해 진짜 가운데로 뭉치게 한다.
                    카드 높이가 고정(INFO_H)이라 중앙정렬해도 카드 간 정렬이 깔끔. Flags는 flexWrap 무제한 → 1줄 슬롯으로 바운딩. */}
                <VStack style={[styles.cardInfo, isRow && { alignSelf: "stretch" }, m.soldOut && { opacity: 0.5 }]}>
                    {anyFlag && <View style={styles.cardFlagSlot}><Flags m={m} compact={!isRow} /></View>}
                    <Text style={styles.cardName} numberOfLines={2}>{m.name}</Text>
                    {/* 칩+이름2줄+설명2줄+가격이 고정높이(INFO_H)를 넘쳐 이름·설명이 잘리던 문제.
                        LIST·CARDS 모두 설명을 1줄로 줄여 세로 여유 확보(이름은 2줄 유지). */}
                    {!!m.descr && <Text style={styles.cardDesc} numberOfLines={1}>{m.descr}</Text>}
                    <Price saleMode={saleMode} menu={m} />
                </VStack>
            </Box>
        </TouchableWithoutFeedback>
    );
}

// 주류 카테고리/메뉴 첫 접근 시 세션 1회 표시하는 법적 경고 이미지(주별). I Understand로만 닫힘.
export function AlcoholWarning({ uri, onClose }: { uri: { uri: string }; onClose: () => void }) {
    const accent = apps.getAccent();
    const onAccent = apps.onAccent();
    return (
        <Modal visible transparent animationType="fade" onRequestClose={onClose}>
            <View style={styles.alcoholBackdrop}>
                <Image source={uri} alt="" resizeMode="contain" style={styles.alcoholImage} />
                <TouchableOpacity onPress={onClose} style={[styles.alcoholOkBtn, { backgroundColor: accent }]}>
                    <Text style={[styles.alcoholOkText, { color: onAccent }]}>I Understand</Text>
                </TouchableOpacity>
            </View>
        </Modal>
    );
}

const styles = StyleSheet.create({
    // D2 mockup card (redesign-mockups.html .card 83-105)
    card: {
        backgroundColor: PALETTE.CARD_BG,
        borderWidth: 1,
        borderColor: PALETTE.CARD_BORDER,
        borderRadius: PALETTE.CARD_RADIUS,
        overflow: "hidden",
        position: "relative",
        marginTop: 12, // inter-row gap (card is inside the fixed-height wrapper, so this never shifts getItemLayout offsets)
        ...PALETTE.CARD_SHADOW,
    },
    cardRow: {
        flexDirection: "row",
        alignItems: "flex-start", // keep thumb square; info grows below name/desc
    },
    cardCol: {
        flexDirection: "column",
    },
    cardThumb: {
        backgroundColor: PALETTE.THUMB_BG,
        overflow: "hidden",
        position: "relative",
    },
    cardInfo: {
        flex: 1,
        minWidth: 0,
        padding: 12,
        gap: 3,   // 칩/이름/설명/가격 간격 축소(사용자 요청)
        justifyContent: "center",   // B1 후속: 내용 세로 중앙 정렬(카드 높이 고정이라 카드 간 정렬 깔끔)
    },
    cardName: {
        fontFamily: "NotoSans-Bold",
        fontSize: 15,
        fontWeight: "700",
        color: PALETTE.TEXT,
        lineHeight: 19,
        letterSpacing: -0.1,
    },
    cardDesc: {
        fontFamily: "NotoSans-Regular",
        fontSize: 12,
        lineHeight: 17,
        color: PALETTE.MUTED,
    },
    // B1: Flags 고정 1줄 슬롯 — 빈 경우 spacer, 3칩이 2줄로 늘어나도 clip(가격줄 밀림 방지).
    cardFlagSlot: {
        height: 20,
        overflow: "hidden",
        justifyContent: "center",
    },
    // D2 mockup pricerow (redesign-mockups.html 96-99)
    priceRow: {
        alignItems: "baseline",
        gap: 7,
    },
    priceNormal: {
        fontFamily: "NotoSans-Bold",
        fontSize: 15.5,
        fontWeight: "800",
        color: PALETTE.PRICE,
    },
    priceOld: {
        fontFamily: "NotoSans-Regular",
        fontSize: 12.5,
        color: PALETTE.MUTED,
        textDecorationLine: "line-through",
    },
    priceNow: {
        fontFamily: "NotoSans-Bold",
        fontSize: 15.5,
        fontWeight: "800",
        color: PALETTE.SALE,
    },
    // D2: full-cover sold-out overlay on the thumb (mockup .soldout 103-104)
    soldOutOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100%',
        height: '100%',
        zIndex: 2,
    },
    alcoholBackdrop: {
        flex: 1,
        backgroundColor: "rgba(0,0,0,0.88)",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
    },
    alcoholImage: {
        width: "100%",
        maxWidth: 560,
        height: "78%",
    },
    alcoholOkBtn: {
        marginTop: 22,
        paddingVertical: 13,
        paddingHorizontal: 36,
        borderRadius: PALETTE.RADIUS_PILL,
    },
    alcoholOkText: {
        fontFamily: "NotoSans-Bold",
        fontSize: 16,
        fontWeight: "800",
    },
});
